import {useState} from "react";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import type {DungeonLevel} from "@/dungeons/model/DungeonLevel.ts";
import type {LevelTransition} from "@/dungeons/model/DungeonGeometry.ts";
import {transitionArrivalWorld} from "@/dungeons/geometry/TransitionArrival.ts";
import {placeCombatantsAtArrival} from "./LevelTransitionPlacement.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

type PendingTransition = {
    sourceLevelId: string;
    transition: LevelTransition;
    combatantIds: string[];
};

export default function useEncounterLevelTransition(activeLevel?: DungeonLevel) {
    const {dungeon, dungeonSettings, setDungeonSettings} = useDungeon();
    const {combatants, update} = useCombatants();
    const {selectedCombatants} = useTableTopSelection();
    const {focusWorldPosition} = useEncounterViewPort();
    const [pending, setPending] = useState<PendingTransition>();
    const [busy, setBusy] = useState(false);
    const selectedCombatantIds = activeLevel
        ? selectedCombatants.filter(combatant => combatant.levelId === activeLevel.id)
            .map(combatant => combatant.id)
        : [];
    const canSelect = selectedCombatantIds.length > 0;
    const targetLevel = dungeon.levels.find(level => level.id === pending?.transition.targetLevelId);
    const selectedForTransition = pending
        ? combatants.filter(combatant => pending.combatantIds.includes(combatant.id) &&
            combatant.levelId === pending.sourceLevelId)
        : [];
    const arrival = pending && targetLevel ? transitionArrivalWorld(pending.transition, targetLevel.geometry) : undefined;
    const ready = Boolean(pending && targetLevel && arrival && selectedForTransition.length > 0 &&
        dungeonSettings.levelId === pending.sourceLevelId);

    const select = (transition: LevelTransition) => {
        if (!activeLevel || !canSelect) return;
        setPending({
            sourceLevelId: activeLevel.id,
            transition,
            combatantIds: selectedCombatantIds,
        });
    };

    const close = () => {
        if (busy) return;
        setPending(undefined);
    };

    const confirm = async () => {
        if (!pending || !targetLevel || !arrival || busy || selectedForTransition.length === 0 ||
            dungeonSettings.levelId !== pending.sourceLevelId) return;
        setBusy(true);
        try {
            await update(placeCombatantsAtArrival(selectedForTransition, targetLevel.id, arrival), "immediate");
            if (targetLevel.id !== dungeonSettings.levelId) {
                await setDungeonSettings({...dungeonSettings, levelId: targetLevel.id});
            }
            focusWorldPosition(arrival);
            setPending(undefined);
        } catch (error) {
            notifyError(error, "Der Levelübergang konnte nicht abgeschlossen werden.");
        } finally {
            setBusy(false);
        }
    };

    return {pending, targetLevel, selectedForTransition, ready, busy, canSelect, select, close, confirm};
}
